import React, { useEffect, useRef, useState } from "react";
import 'bootstrap/dist/css/bootstrap.min.css';
import "leaflet/dist/leaflet.css";
import { Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import Footer from "./footer";
import Header from "./header";
import { studentview, vehicleLocationLatest } from "../service/dashboard";
import moment from "moment";

// How often the parent app re-fetches the bus position (ms).
const POLL_INTERVAL = 15000;
// Fallback map centre (used until we have a real fix). Roughly central India.
const DEFAULT_CENTER = [20.5937, 78.9629];

// A Font Awesome bus marker so we don't depend on leaflet's default PNG assets
// (which break under webpack without extra config).
const busIcon = L.divIcon({
  className: "bus-marker",
  html: '<div style="background:#198754;color:#fff;width:38px;height:38px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,.4);"><i class="fas fa-bus" style="transform:rotate(45deg);font-size:16px;"></i></div>',
  iconSize: [38, 38],
  iconAnchor: [19, 38],
  popupAnchor: [0, -36],
});

// Keeps the map centred on the bus as it moves.
function Recenter({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.setView(position, map.getZoom() < 14 ? 15 : map.getZoom());
    }
  }, [position, map]);
  return null;
}

const VehicleTracking = () => {
  const [student, setStudent] = useState(null);   // student_details (has vehicle_id etc.)
  const [loc, setLoc] = useState(null);           // latest location row from server
  const [position, setPosition] = useState(null); // [lat, lng] for the marker
  const [message, setMessage] = useState("Loading…");
  const timerRef = useRef(null);

  useEffect(() => {
    const uid = localStorage.getItem("UID");
    studentview(uid).then((res) => {
      if (res && res.status === true && res.data.details) {
        const det = res.data.details;
        setStudent(det);
        if (!det.vehicle_id) {
          setMessage("No bus is mapped to this student.");
          return;
        }
        fetchLocation(det.vehicle_id);
        timerRef.current = setInterval(() => fetchLocation(det.vehicle_id), POLL_INTERVAL);
      } else {
        setMessage("Unable to load student details.");
      }
    });

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function fetchLocation(vehicleId) {
    const temp = new FormData();
    temp.append("vehicle_id", vehicleId);
    vehicleLocationLatest(temp).then((res) => {
      if (res && res.status === true && res.data && res.data.details) {
        const d = res.data.details;
        setLoc(d);
        setPosition([parseFloat(d.latitude), parseFloat(d.longitude)]);
        setMessage("");
      } else {
        // Endpoint returns status:true with details:null when the trip is not
        // active, and status:false when access is denied.
        setLoc(null);
        setPosition(null);
        setMessage(
          res && res.status === false && res.message
            ? res.message
            : "Bus is not sharing location right now. The trip may not have started yet."
        );
      }
    });
  }

  const driverName = loc?.worker_name || student?.worker_name;
  const vehicleNo = loc?.vehicle_no || student?.vehicle_no;
  const routeName = loc?.vehroute_name || student?.vehroute_name;
  const driverPhone = loc?.worker_phone || student?.worker_phone;

  return (
    <div id="mainpage">
      <div className="maincontent">
        <Header />

        <div className="moreheading">
          <Row className="row-sm align-items-center">
            <Col xs={12} sm={6} className="text-left mb-2 mb-sm-0">
              <h4 className="morehead-text mb-0">Track Bus</h4>
            </Col>
            <Col xs={12} sm={6}>
              <div className="d-flex flex-wrap justify-content-start justify-content-sm-end gap-2">
                <Link to={"/dashboard"} className="btn-outline-success red-btn btn-sm">
                  <i className="fa-solid fa-hand-point-left"></i> Go Back
                </Link>
              </div>
            </Col>
          </Row>
        </div>

        <div className="morecontent-section mx-3 mb-3">
          <h4>Transport Details</h4>
          <div className="traveldetails">
            <h3>Vehicle Number<span>: {vehicleNo || "-"}</span></h3>
            <h3>Driver<span>: {driverName || "-"}</span></h3>
            <h3>Vehicle Route<span>: {routeName || "-"}</span></h3>
            <h3>Mobile No<span>: {driverPhone || "-"}</span></h3>
          </div>
          {driverPhone && (
            <a href={`tel:${driverPhone}`} className="btn btn-success btn-sm mt-2">
              <i className="fa-solid fa-phone me-2"></i> Call Driver
            </a>
          )}
        </div>

        <div className="mx-3 mb-3">
          <div style={{ height: "60vh", width: "100%", borderRadius: "8px", overflow: "hidden" }}>
            <MapContainer
              center={position || DEFAULT_CENTER}
              zoom={position ? 15 : 5}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution='&copy; OpenStreetMap contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {position && (
                <>
                  <Marker position={position} icon={busIcon}>
                    <Popup>
                      {vehicleNo || "Bus"}
                      <br />
                      {driverName}
                    </Popup>
                  </Marker>
                  <Recenter position={position} />
                </>
              )}
            </MapContainer>
          </div>

          {loc && (
            <div className="text-center small text-muted mt-2">
              <i className="fa-solid fa-circle text-success me-1" style={{ fontSize: "8px" }}></i>
              Last updated: {moment(loc.recorded_at).format("DD-MM-YYYY hh:mm:ss A")}
              {" · "}({moment(loc.recorded_at).fromNow()})
            </div>
          )}
          {message && (
            <p className="text-center text-danger mt-3">{message}</p>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default VehicleTracking;
